import React, { useEffect, useState } from "react";
import api, { formatErrorDetail } from "@/lib/api";
import { Surface, Empty, TableWrap } from "@/components/app/Primitives";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import TargetPicker, { loadTargetOptions } from "./TargetPicker";
import { manilaToday } from "@/lib/date";

export default function ShiftAssignments({ shifts, reload }) {
    const [items, setItems] = useState([]);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({ shift_id: "", target_type: "department", target_id: "",
                                       effective_from: manilaToday(),
                                       effective_to: "", note: "" });
    // {target_type: {target_id: name}} — resolved lazily as rows arrive so the
    // "Applies to" column reads "Warehouse Ops" instead of a raw id.
    const [targetNames, setTargetNames] = useState({});

    const load = () => api.get("/shifts/assignments")
        .then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
    useEffect(() => { load(); }, []);

    useEffect(() => {
        const types = [...new Set(items.map((a) => a.target_type))].filter((t) => !targetNames[t]);
        types.forEach((t) => {
            loadTargetOptions(t).then((rows) => {
                const map = {};
                rows.forEach((o) => { map[o.id] = o.name; });
                setTargetNames((prev) => ({ ...prev, [t]: map }));
            });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items]);

    const shiftName = (id) => shifts.find((s) => s.id === id)?.name || id;
    // Falls back to the raw id (never blank) when the lookup hasn't loaded
    // yet or the target itself is gone (e.g. a deleted department).
    const targetLabel = (a) => a.target_name || targetNames[a.target_type]?.[a.target_id] || a.target_id;

    const save = async () => {
        if (!form.shift_id) return toast.error("Pick a shift");
        if (!form.target_id) return toast.error("Pick who this applies to");
        try {
            await api.post("/shifts/assignments", { ...form, effective_to: form.effective_to || null });
            toast.success("Assignment created");
            setCreating(false); load(); reload();
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
    };

    const revoke = async (a) => {
        if (!window.confirm("Revoke this assignment? People it reaches fall back to the next rule.")) return;
        try { await api.delete(`/shifts/assignments/${a.id}`); toast.success("Revoked"); load(); reload(); }
        catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
    };

    return (
        <Surface className="p-4">
            <div className="flex items-center justify-between mb-3">
                <div className="text-xs text-muted-foreground">
                    A more specific rule wins: employee beats group beats position beats department
                    beats branch beats employment type beats company. Within one level, the shorter
                    date window wins.
                </div>
                <button className="h-9 px-3 rounded-md bg-primary text-white text-sm font-medium flex items-center gap-2"
                        onClick={() => setCreating(true)} data-testid="new-assignment-btn"><Plus className="size-4" /> New rule</button>
            </div>

            <TableWrap>
                <thead><tr><th>Shift</th><th>Applies to</th><th>From</th><th>Until</th><th>Note</th><th></th></tr></thead>
                <tbody>
                    {items.map((a) => (
                        <tr key={a.id} className={a.revoked_at ? "opacity-40" : ""} data-testid={`assignment-${a.id}`}>
                            <td>{shiftName(a.shift_id)}</td>
                            <td><span className="badge">{a.target_type}</span> {targetLabel(a)}</td>
                            <td className="font-mono text-xs">{a.effective_from}</td>
                            <td className="font-mono text-xs">{a.effective_to || "open"}</td>
                            <td className="text-xs text-muted-foreground">{a.note}</td>
                            <td>{!a.revoked_at &&
                                <button className="text-destructive text-xs" onClick={() => revoke(a)}>Revoke</button>}</td>
                        </tr>
                    ))}
                    {!items.length && <tr><td colSpan={6}>
                        <Empty title="No assignment rules yet" subtitle="Nobody is on a shift until a rule reaches them." />
                    </td></tr>}
                </tbody>
            </TableWrap>

            <Dialog open={creating} onOpenChange={setCreating}>
                <DialogContent className="max-w-lg">
                    <DialogHeader><DialogTitle>New assignment rule</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label>Shift</Label>
                            <select className="h-9 w-full rounded-md border px-2 text-sm bg-background"
                                    value={form.shift_id} data-testid="assignment-shift"
                                    onChange={(e) => setForm({ ...form, shift_id: e.target.value })}>
                                <option value="">Select…</option>
                                {shifts.filter((s) => s.is_active !== false).map((s) =>
                                    <option key={s.id} value={s.id}>{s.name} ({s.start_time}–{s.end_time})</option>)}
                            </select>
                        </div>
                        <TargetPicker targetType={form.target_type} targetId={form.target_id}
                                      onChange={(t, i) => setForm({ ...form, target_type: t, target_id: i })} />
                        <div className="grid grid-cols-2 gap-2">
                            <div><Label>Effective from</Label><Input type="date" value={form.effective_from}
                                onChange={(e) => setForm({ ...form, effective_from: e.target.value })} /></div>
                            <div><Label>Until (blank = open)</Label><Input type="date" value={form.effective_to}
                                onChange={(e) => setForm({ ...form, effective_to: e.target.value })} /></div>
                        </div>
                        <div><Label>Note</Label><Input value={form.note} placeholder="why this rule exists"
                            onChange={(e) => setForm({ ...form, note: e.target.value })} /></div>
                    </div>
                    <DialogFooter>
                        <button className="h-9 px-3 rounded-md bg-primary text-white text-sm"
                                onClick={save} data-testid="assignment-save">Create rule</button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Surface>
    );
}
